import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  LinearProgress,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Alert,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TablePagination,
  Menu,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  ArrowDropDown,
  CheckCircle,
  Cancel,
  Edit,
  Save,
  Close,
  Visibility,
  OpenInNew,
  Download,
  AutoAwesome,
  Autorenew,
  PostAdd,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";
import api from "../../api";
import CategorySaveFields from "./components/CategorySaveFields";
import { QUESTION_TYPES } from "./components/QuestionTypeSelector";
import HtmlWithLatex from "./components/HtmlWithLatex";
import QuestionAnswerDisplay from "./components/QuestionAnswerDisplay";
import QuestionPaperLayout from "./components/QuestionPaperLayout";
import DifficultyChip from "./components/DifficultyChip";
import { questionHasRenderableAnswer as questionHasAnswer } from "./utils/questionHasRenderableAnswer";
import PassageWithLineNumbers from "./components/PassageWithLineNumbers";


// ── helpers ──────────────────────────────────────────────────────────────

const stripHtml = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

/** Stem / question image URLs: prefer image_urls[], fallback image_url */
const collectImageUrls = (q) => {
  if (!q) return [];
  const arr = Array.isArray(q.image_urls) ? q.image_urls.filter(Boolean) : [];
  if (arr.length) return arr;
  return q.image_url ? [q.image_url] : [];
};

const buildVerificationSummary = (report) => {
  if (!report) return '';
  const parts = [];
  if (report.answer_gaps > 0) parts.push(`${report.answer_gaps} answer gap(s)`);
  if (report.checks?.count_mismatch) parts.push('question count mismatch');
  if (report.checks?.parity_issues?.length) parts.push(`${report.checks.parity_issues.length} parity issue(s)`);
  return parts.length ? parts.join(', ') : 'issues found';
};

const typeColors = {
  "multiple-choice": "#1976d2",
  "multiple-select": "#7b1fa2",
  "true-false": "#388e3c",
  "fill-in-the-blank": "#f57c00",
  "multi-fill": "#e64a19",
  matching: "#0097a7",
  ordering: "#5d4037",
  essay: "#c2185b",
  "short-answer": "#455a64",
  grouped: "#6a1b9a",
};

// ══════════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════════

const SimilarPaperReview = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── list vs review mode — derived from URL so there is no race condition ──
  const jobIdParam = searchParams.get("job");
  const mode = jobIdParam ? "review" : "list";

  // ── list state ──
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsTotal, setJobsTotal] = useState(0);
  const jobsPage = Math.max(parseInt(searchParams.get("page") || "1", 10), 1) - 1;
  const jobsPerPage = Math.max(parseInt(searchParams.get("per_page") || "15", 10), 1);
  const jobsSearch = searchParams.get("search") || "";
  const jobsStatus = searchParams.get("status") || "";
  const jobsSource = searchParams.get("source") || "";
  const jobsDifficulty = searchParams.get("difficulty") || "";
  const jobsVerificationStatus = searchParams.get("verification_status") || "";
  const [jobsSearchInput, setJobsSearchInput] = useState(jobsSearch);

  // ── review state ──
  const [jobData, setJobData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [modifiedSet, setModifiedSet] = useState(new Set());
  const [reviewLoading, setReviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── edit state ──
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);

  // ── generate similar paper state ──
  const [genOpen, setGenOpen] = useState(false);
  const [genning, setGenning] = useState(false);
  const [genAsync, setGenAsync] = useState(true);
  const [genCount, setGenCount] = useState(10);
  const [genDifficulty, setGenDifficulty] = useState("Mixed");
  const [genSource, setGenSource] = useState("ai");
  const [saveCategoryId, setSaveCategoryId] = useState("");
  const [saveSourceType, setSaveSourceType] = useState("ai_generated");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [selectedBankIds, setSelectedBankIds] = useState([]);
  const [bankSearchQuery, setBankSearchQuery] = useState("");
  const [bankSearchResults, setBankSearchResults] = useState([]);
  const [bankSearchLoading, setBankSearchLoading] = useState(false);
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [genStatus, setGenStatus] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [genError, setGenError] = useState(null);
  const [genResultOpen, setGenResultOpen] = useState(false);
  const [genQuestions, setGenQuestions] = useState([]);
  const [genJobId, setGenJobId] = useState(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingSimilarPaper, setSavingSimilarPaper] = useState(false);
  const [similarPaperSaved, setSimilarPaperSaved] = useState(false);
  const [similarPaperSavedIds, setSimilarPaperSavedIds] = useState([]);
  const [genVerification, setGenVerification] = useState(null);
  const pollRef = useRef(null);
  const listPollRef = useRef(null);

  // AI diagram (Import Review parity — ai-generate-diagram + upload-image)
  const [diagramDialogOpen, setDiagramDialogOpen] = useState(false);
  const [generatingDiagram, setGeneratingDiagram] = useState(false);
  const [imageUploadingDiagram, setImageUploadingDiagram] = useState(false);
  const [generatedDiagram, setGeneratedDiagram] = useState(null);
  /** @type {{ type: 'stem' | 'sub' | 'standalone'; subIndex?: number } | null} */
  const [diagramTargetPath, setDiagramTargetPath] = useState(null);

  // ── per-question regeneration + topic hint ──
  const [genTopicHint, setGenTopicHint] = useState("");
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);

  // ── Create As (Mock Exam / Past Paper / Worksheet) ──
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [createTarget, setCreateTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", format_id: "", school_year_id: "", topic_id: "", price: 0 });
  const [lookupFormats, setLookupFormats] = useState([]);
  const [lookupSchoolYears, setLookupSchoolYears] = useState([]);
  const [lookupTopics, setLookupTopics] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // ════════════════════════════════════════════════════════════════════════
  // LIST MODE
  // ════════════════════════════════════════════════════════════════════════

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.get("/admin/question-bank/similar-paper-jobs", {
        params: {
          page: jobsPage + 1,
          per_page: jobsPerPage,
          search: jobsSearch || undefined,
          status: jobsStatus || undefined,
          source: jobsSource || undefined,
          difficulty: jobsDifficulty || undefined,
          verification_status: jobsVerificationStatus || undefined,
        },
      });
      const data = res.data?.data;
      setJobs(data?.data || []);
      setJobsTotal(data?.total || 0);
    } catch {
      toast.error("Failed to load similar paper jobs");
    } finally {
      setJobsLoading(false);
    }
  }, [jobsDifficulty, jobsPage, jobsPerPage, jobsSearch, jobsSource, jobsStatus, jobsVerificationStatus]);

  useEffect(() => {
    if (mode === "list") fetchJobs();
  }, [mode, fetchJobs]);

  // Auto-refresh list while any jobs are pending/processing
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === "pending" || j.status === "processing");
    if (hasActive && !listPollRef.current) {
      listPollRef.current = setInterval(() => fetchJobs(), 10000);
    } else if (!hasActive && listPollRef.current) {
      clearInterval(listPollRef.current);
      listPollRef.current = null;
    }
    return () => {
      if (listPollRef.current) {
        clearInterval(listPollRef.current);
        listPollRef.current = null;
      }
    };
  }, [jobs, fetchJobs]);

  useEffect(() => {
    setJobsSearchInput(jobsSearch);
  }, [jobsSearch]);

  const updateListParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (!next.get("job")) {
      setSearchParams(next);
      return;
    }
    setSearchParams(next);
  };

  // ════════════════════════════════════════════════════════════════════════
  // REVIEW MODE
  // ════════════════════════════════════════════════════════════════════════

  const loadJob = useCallback(async (id) => {
    setReviewLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/similar-paper-job/${id}/preview`);
      const d = res.data?.data;
      // Merge import metadata (exam_title, source_filename, subject_id, school_name)
      // under the job so the Create-As dialog and headers can read them; job.id wins.
      setJobData({ ...(d.import || {}), ...d.job });
      setSaveSourceType(d.job?.target_source_type || "ai_generated");
      setSaveCategoryId(d.job?.target_category_id || d.import?.target_category_id || "");
      setQuestions(d.parsed_questions || []);
      setCurrentIndex(0);
      setApprovedSet(new Set());
      setModifiedSet(new Set());
      setEditMode(false);
      setEditDraft(null);
    } catch {
      toast.error("Failed to load similar paper job");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "review" && jobIdParam) {
      loadJob(jobIdParam);
    }
  }, [mode, jobIdParam, loadJob]);

  const openReview = (id) => {
    setSearchParams({ job: id });
  };

  const backToList = () => {
    setSearchParams({});
    setJobData(null);
    setQuestions([]);
  };

  // ── navigation ──
  const goTo = (idx) => {
    if (idx >= 0 && idx < questions.length) {
      setCurrentIndex(idx);
      setEditMode(false);
      setEditDraft(null);
    }
  };

  // ── approve toggle ──
  const toggleApproval = (idx) => {
    setApprovedSet((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const approveAll = () => {
    setApprovedSet(new Set(questions.map((_, i) => i)));
  };

  const rejectAll = () => setApprovedSet(new Set());

  // ── regenerate a single question slot ──
  const handleRegenerate = async (idx) => {
    if (!jobData?.id) return;
    setRegeneratingIdx(idx);
    try {
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/regenerate-slot/${idx}`);
      const nq = res.data?.data?.question;
      if (nq) {
        setQuestions((prev) => prev.map((q, i) => (i === idx ? nq : q)));
        setModifiedSet((prev) => new Set(prev).add(idx));
        setApprovedSet((prev) => {
          const n = new Set(prev);
          n.delete(idx);
          return n;
        });
        toast.success(`Question ${idx + 1} regenerated`);
      } else {
        toast.warning("Regeneration returned no question");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Regeneration failed");
    } finally {
      setRegeneratingIdx(null);
    }
  };

  // ── Create As (Mock Exam / Past Paper / Worksheet) ──
  const fetchEntityLookups = useCallback(async (target) => {
    setLookupsLoading(true);
    try {
      if (target === "mock_exam" || target === "paper") {
        const [f, y] = await Promise.all([
          api.get("common/data?param=Formats"),
          api.get("common/data?param=SchoolYears"),
        ]);
        setLookupFormats(f.data?.data || f.data || []);
        setLookupSchoolYears(y.data?.data || y.data || []);
      } else if (target === "worksheet") {
        const subjectId = jobData?.subject_id;
        if (subjectId) {
          const t = await api.get("/admin/question-bank/topics-by-subject", { params: { subject_id: subjectId } });
          setLookupTopics(t.data?.data || []);
        } else {
          setLookupTopics([]);
        }
      }
    } catch {
      toast.error("Failed to load options");
    } finally {
      setLookupsLoading(false);
    }
  }, [jobData]);

  const openCreateDialog = (target) => {
    setCreateMenuAnchor(null);
    setCreateTarget(target);
    const defName = jobData?.exam_title || jobData?.source_filename || `Similar Paper #${jobData?.id}`;
    setCreateForm({ name: defName, format_id: "", school_year_id: "", topic_id: "", price: 0 });
    setCreateOpen(true);
    fetchEntityLookups(target);
  };

  const handleCreateEntity = async () => {
    if (!jobData?.id || !createTarget) return;
    if (!createForm.name.trim()) {
      toast.warning("Name is required");
      return;
    }
    if ((createTarget === "mock_exam" || createTarget === "paper") && (!createForm.format_id || !createForm.school_year_id)) {
      toast.warning("Format and School Year are required");
      return;
    }
    if (createTarget === "worksheet" && !createForm.topic_id) {
      toast.warning("Topic is required");
      return;
    }
    setCreating(true);
    try {
      const payload = { target: createTarget, name: createForm.name.trim() };
      if (createTarget === "mock_exam" || createTarget === "paper") {
        payload.format_id = createForm.format_id;
        payload.school_year_id = createForm.school_year_id;
        payload.price = createForm.price || 0;
      }
      if (createTarget === "worksheet") payload.topic_id = createForm.topic_id;

      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/create-entity`, payload);
      const d = res.data?.data;
      toast.success(res.data?.message || "Created successfully");
      setCreateOpen(false);
      if (d?.edit_path) navigate(d.edit_path);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const CREATE_TARGET_LABELS = { mock_exam: "Mock Exam", paper: "Past Paper", worksheet: "Worksheet" };

  // ── edit ──
  const startEdit = () => {
    const q = questions[currentIndex];
    setEditDraft(JSON.parse(JSON.stringify(q)));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditDraft(null);
  };

  const saveEdit = () => {
    if (!editDraft) return;
    const updated = [...questions];
    updated[currentIndex] = editDraft;
    setQuestions(updated);
    setModifiedSet((prev) => new Set(prev).add(currentIndex));
    setEditMode(false);
    setEditDraft(null);
    toast.success("Question updated (locally)");
  };

  const updateDraftField = (field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  };

  // ── save draft to server ──
  const saveDraft = async () => {
    if (!jobData) return;
    try {
      await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/update-questions`, {
        generated_questions: questions,
      });
      toast.success("Draft saved");
    } catch {
      toast.error("Failed to save draft");
    }
  };

  const persistQuestions = async (nextQuestions) => {
    if (!jobData) return;
    await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/update-questions`, {
      generated_questions: nextQuestions,
    });
  };

  const handleAiGenerateDiagram = async (target) => {
    const importId = jobData?.import_id;
    if (!importId) {
      toast.error("No source import linked to this job");
      return;
    }
    let q;
    if (target.type === "stem") {
      q = questions[currentIndex];
    } else if (target.type === "sub") {
      q = questions[currentIndex]?.sub_questions?.[target.subIndex];
    } else {
      q = questions[currentIndex];
    }
    if (!q) {
      toast.error("Question not found");
      return;
    }
    setGeneratingDiagram(true);
    setGeneratedDiagram(null);
    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/ai-generate-diagram`, {
        question: q,
      });
      setGeneratedDiagram(res.data?.data ?? null);
      setDiagramTargetPath(target);
      setDiagramDialogOpen(true);
    } catch {
      toast.error("AI diagram generation failed. Please try again.");
    } finally {
      setGeneratingDiagram(false);
    }
  };

  const applyGeneratedDiagram = async () => {
    if (!generatedDiagram?.svg || !diagramTargetPath || !jobData?.import_id) return;

    setImageUploadingDiagram(true);
    try {
      const svgBlob = new Blob([generatedDiagram.svg], { type: "image/svg+xml" });
      const svgFile = new File([svgBlob], `diagram_${Date.now()}.svg`, { type: "image/svg+xml" });
      const formData = new FormData();
      formData.append("image", svgFile);

      const res = await api.post(
        `/admin/question-bank/import/${jobData.import_id}/upload-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const imageUrl = res.data?.data?.image_url;
      if (!imageUrl) {
        toast.error("Diagram upload failed — no URL returned");
        return;
      }

      const diagType = generatedDiagram?.diagram_type ?? null;
      const hubVal = generatedDiagram?.hub_value ?? null;

      const next = structuredClone(questions);
      const idx = currentIndex;
      const applyToQuestion = (qq) => {
        qq.image_url = imageUrl;
        qq.image_urls = [imageUrl];
        if (diagType) {
          qq.answer_data = {
            ...(qq.answer_data || {}),
            diagram_type: diagType,
            hub_value: hubVal,
          };
        }
      };

      if (diagramTargetPath.type === "stem") {
        applyToQuestion(next[idx]);
      } else if (diagramTargetPath.type === "sub") {
        applyToQuestion(next[idx].sub_questions[diagramTargetPath.subIndex]);
      } else {
        applyToQuestion(next[idx]);
      }

      setQuestions(next);
      setModifiedSet((prev) => new Set(prev).add(idx));
      await persistQuestions(next);

      setDiagramDialogOpen(false);
      setGeneratedDiagram(null);
      setDiagramTargetPath(null);
      toast.success("Diagram added to question");
    } catch {
      toast.error("Failed to upload diagram. Please try again.");
    } finally {
      setImageUploadingDiagram(false);
    }
  };

  // ── confirm import ──
  const handleConfirm = async () => {
    const approvedIndices = [...approvedSet].sort((a, b) => a - b);
    if (approvedIndices.length === 0) {
      toast.warning("No questions approved");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/admin/question-bank/similar-paper-job/${jobData.id}/confirm`, {
        approved_indices: approvedIndices,
        questions,
        target_source_type: saveSourceType,
        target_category_id: saveCategoryId || undefined,
      });
      const d = res.data?.data;
      if (d?.already_imported) {
        toast.info(`Already imported (${d.questions_imported} questions)`);
      } else {
        toast.success(`${d.questions_imported} questions imported to Question Bank`);
      }
      setSaveDialogOpen(false);
      backToList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to import questions");
    } finally {
      setSubmitting(false);
    }
  };

  // ── generate similar paper ──
  const stopPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopPoll(), []);

  const startPoll = (importId, jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const params = jobId ? `?job_id=${jobId}` : "";
        const res = await api.get(`/admin/question-bank/import/${importId}/similar-paper-status${params}`);
        const s = res.data?.data;
        if (!s) return;
        setGenStatus(s.status);
        setGenProgress(s.progress || 0);
        if (s.status === "completed") {
          stopPoll();
          setGenQuestions(s.questions || []);
          setGenVerification({
            status: s.verification_status ?? null,
            report: s.verification_report ?? null,
          });
          if (s.filename) {
            setGeneratedPdfUrl(`/admin/question-bank/import/${importId}/similar-paper/${s.filename}`);
          }
          toast.success("Similar paper generated!");
        } else if (s.status === "failed") {
          stopPoll();
          setGenError(s.error || "Generation failed");
          toast.error("Generation failed");
        }
      } catch {
        // silently retry
      }
    }, 3000);
  };

  // ── bank question search (mirrors QuestionBankImportReview) ──────────────
  const searchBankQuestions = useCallback(async (q) => {
    setBankSearchLoading(true);
    try {
      const res = await api.get("/admin/question-bank", { params: { search: q, per_page: 50 } });
      setBankSearchResults(res.data?.data?.data || []);
    } catch {
      // silently ignore search errors
    } finally {
      setBankSearchLoading(false);
    }
  }, []);

  const toggleBankId = useCallback((id) => {
    setSelectedBankIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleGenerate = async () => {
    const importId = jobData?.import_id;
    if (!importId) {
      toast.error("No source import linked to this job");
      return;
    }
    setGenning(true);
    setGenError(null);
    setGenStatus(null);
    setGenProgress(0);
    setGenQuestions([]);
    setGenJobId(null);
    setGeneratedPdfUrl(null);
    setSimilarPaperSaved(false);
    setSimilarPaperSavedIds([]);
    setGenVerification(null);

    const payload = {
      count: ["bank", "variation"].includes(genSource) ? selectedBankIds.length : genCount,
      difficulty: ["bank", "variation"].includes(genSource) ? "Mixed" : genDifficulty,
      source: genSource,
      question_ids: genSource !== "ai" ? selectedBankIds : undefined,
      shuffle: shuffleQuestions,
      shuffle_options: shuffleOptions,
      async: genAsync,
      topic_hint: genTopicHint.trim() || undefined,
      target_source_type: saveSourceType,
      target_category_id: saveCategoryId || undefined,
    };

    try {
      const res = await api.post(`/admin/question-bank/import/${importId}/generate-similar-paper`, payload);
      const d = res.data?.data;
      const newJobId = d?.similar_paper_job_id;
      setGenJobId(newJobId);

      if (genAsync) {
        setGenStatus("pending");
        setGenOpen(false);
        setGenResultOpen(true);
        startPoll(importId, newJobId);
      } else {
        setGenQuestions(d?.questions || []);
        setGenStatus("completed");
        setGenJobId(d?.similar_paper_job_id ?? null);
        setGenVerification({
          status: d?.verification_status ?? null,
          report: d?.verification_report ?? null,
        });
        if (d?.download_filename && jobData?.import_id) {
          setGeneratedPdfUrl(`/admin/question-bank/import/${jobData.import_id}/similar-paper/${d.download_filename}`);
        }
        setGenOpen(false);
        // Auto-open the freshly generated job in the review screen instead of
        // leaving the user in the result popup.
        if (newJobId) {
          openReview(newJobId);
        } else {
          setGenResultOpen(true);
        }
      }
    } catch (err) {
      setGenError(err.response?.data?.message || "Failed to start generation");
      toast.error(err.response?.data?.message || "Failed to start generation");
    } finally {
      setGenning(false);
    }
  };

  // ── download backend-rendered PDF (Browsershot output) ──
  const handleDownloadBackendPdf = async () => {
    const importId = jobData?.import_id;
    let pdfUrl = generatedPdfUrl;

    // For review mode: use job's download_filename
    if (!pdfUrl && jobData?.download_filename && importId) {
      pdfUrl = `/admin/question-bank/import/${importId}/similar-paper/${jobData.download_filename}`;
    }

    // For generate result: try status endpoint if URL not in state
    if (!pdfUrl && importId) {
      try {
        const statusRes = await api.get(`/admin/question-bank/import/${importId}/similar-paper-status`, {
          params: genJobId ? { job_id: genJobId } : {},
        });
        const status = statusRes.data?.data;
        if (status?.filename) {
          pdfUrl = `/admin/question-bank/import/${importId}/similar-paper/${status.filename}`;
        }
      } catch {
        // ignore
      }
    }

    if (!pdfUrl) {
      toast.error("PDF not found. Please generate a new paper.");
      return;
    }

    setDownloadingPdf(true);
    try {
      const res = await api.get(pdfUrl, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "similar-paper.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // ── save similar paper questions to question bank ──
  const handleSaveSimilarPaperToBank = async () => {
    const importId = jobData?.import_id;
    if (!importId) {
      toast.error("No source import linked");
      return;
    }
    setSavingSimilarPaper(true);
    try {
      const payload = genJobId ? { job_id: genJobId } : {};
      const res = await api.post(`/admin/question-bank/import/${importId}/save-similar-paper`, payload);
      const data = res.data?.data;
      if (data?.already_imported) {
        toast.info(`Similar paper questions were already saved (${data.questions_imported} questions).`);
      } else {
        toast.success(`${data?.questions_imported || 0} similar paper questions saved to question bank!`);
      }
      setSimilarPaperSaved(true);
      setSimilarPaperSavedIds(data?.question_ids || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save similar paper questions.");
    } finally {
      setSavingSimilarPaper(false);
    }
  };

  // ── download generated questions as Q&A PDF (client-side HTML, result popup) ──
  const handleDownloadGenPdf = () => {
    if (!genQuestions.length) return;

    const optionLabel = (idx) => String.fromCharCode(65 + idx);

    const renderAnswerHtml = (q) => {
      const ad = q.answer_data || {};
      const opts = q.options || [];
      if (["multiple-choice", "multiple-select", "true-false"].includes(q.type) && opts.length > 0) {
        return `<div class="q-options">${opts.map((opt, i) => {
          const text = opt.text || opt.option_text || "";
          return `<div class="option${opt.is_correct ? " correct" : ""}"><span class="opt-label">${optionLabel(i)}.</span> ${text}${opt.is_correct ? " <span class='tick'>✓</span>" : ""}</div>`;
        }).join("")}</div>`;
      }
      if (["fill-in-the-blank", "short-answer"].includes(q.type) && ad.correct_answer_text)
        return `<div class="q-answer"><strong>Answer:</strong> ${ad.correct_answer_text}</div>`;
      if (q.type === "essay" && ad.model_answer)
        return `<div class="q-answer"><strong>Model Answer:</strong> ${ad.model_answer}</div>`;
      if (q.type === "multi-fill" && Array.isArray(ad.correct_blanks)) {
        const labels = ad.blank_labels || [];
        return `<div class="q-answer">${ad.correct_blanks.map((blank, i) => {
          const ans = Array.isArray(blank) ? blank[0] : blank;
          const alts = Array.isArray(blank) && blank.length > 1 ? ` (also: ${blank.slice(1).join(", ")})` : "";
          const lbl = labels[i] ? `<strong>${labels[i]}</strong>` : `Blank ${i + 1}`;
          return `<div class="blank-row">${lbl}: <span class="correct-ans">${ans}</span>${alts}</div>`;
        }).join("")}</div>`;
      }
      if (q.type === "matching" && opts.length > 0)
        return `<div class="q-answer"><strong>Matching Pairs:</strong>${opts.map(o => `<div class="match-row">${o.text || o.option_text || ""} → <span class="correct-ans">${o.match_text || ""}</span></div>`).join("")}</div>`;
      if (q.type === "ordering" && opts.length > 0)
        return `<div class="q-answer"><strong>Correct Order:</strong>${opts.map((o, i) => `<div class="order-row"><strong>${i + 1}.</strong> ${o.text || o.option_text || ""}</div>`).join("")}</div>`;
      return "";
    };

    const renderBlock = (q, num) => {
      const imgs = (() => { const u = q.image_urls?.length ? q.image_urls : q.image_url ? [q.image_url] : []; return u.length ? `<div class="q-images">${u.map(x => `<img src="${x}" alt="" />`).join("")}</div>` : ""; })();
      return `<div class="question">
        <div class="q-header">
          <span class="q-num">Q${num}</span>
          ${q.type ? `<span class="badge badge-type">${q.type}</span>` : ""}
          ${q.difficulty ? `<span class="badge badge-diff">${q.difficulty}</span>` : ""}
          ${q.default_marks ? `<span class="badge badge-marks">${q.default_marks}m</span>` : ""}
          ${q.topic ? `<span class="badge badge-sub">${q.topic}</span>` : ""}
        </div>
        <div class="q-text">${q.question_text || "<em>(no text)</em>"}</div>
        ${imgs}${renderAnswerHtml(q)}
      </div>`;
    };

    let bodyHtml = "";
    genQuestions.forEach((q, i) => {
      const num = i + 1;
      if (q.type === "grouped") {
        const imgs = (() => { const u = q.image_urls?.length ? q.image_urls : q.image_url ? [q.image_url] : []; return u.length ? `<div class="q-images">${u.map(x => `<img src="${x}" alt="" />`).join("")}</div>` : ""; })();
        bodyHtml += `<div class="question grouped"><div class="q-header"><span class="q-num">Q${num}</span><span class="badge badge-type">grouped</span></div><div class="q-text q-stem"><strong>Shared Stem:</strong><br>${q.question_text || ""}</div>${imgs}<div class="sub-questions">`;
        (q.sub_questions || []).forEach((sq, si) => { bodyHtml += renderBlock(sq, `${num}.${si + 1}`); });
        bodyHtml += `</div></div>`;
      } else {
        bodyHtml += renderBlock(q, num);
      }
    });

    const title = jobData?.exam_title || jobData?.source_filename || `Similar Paper — Job #${jobData?.id}`;
    const subtitle = [jobData?.exam_subject, jobData?.school_name, `Difficulty: ${genDifficulty}`, `AI · ${genQuestions.length} questions`].filter(Boolean).join(" · ");

    const pageHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${title}</title><style>
    *{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;padding:18mm 20mm}
    h1{font-size:15pt;margin-bottom:4px}.subtitle{font-size:9pt;color:#555;margin-bottom:12px}
    hr{border:none;border-top:1px solid #bbb;margin-bottom:16px}
    .question{margin-bottom:18px;padding-bottom:14px;border-bottom:1px solid #ddd;page-break-inside:avoid}
    .question.grouped{border-bottom:2px solid #999}
    .q-header{display:flex;flex-wrap:wrap;align-items:center;gap:5px;margin-bottom:6px}
    .q-num{font-weight:700;font-size:12pt}.badge{display:inline-block;padding:1px 7px;border-radius:3px;font-size:8.5pt}
    .badge-type{background:#e3f2fd;color:#1565c0}.badge-diff{background:#fff3e0;color:#e65100}
    .badge-marks{background:#f3e5f5;color:#6a1b9a}.badge-sub{background:#e8f5e9;color:#1b5e20}
    .q-text{margin-bottom:8px;line-height:1.6}.q-stem{background:#f5f5f5;padding:8px 10px;border-radius:4px;margin-bottom:8px}
    .q-images{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px}.q-images img{max-width:240px;max-height:180px;object-fit:contain;border:1px solid #ddd;border-radius:3px}
    .q-options{margin-bottom:8px}.option{padding:2px 0 2px 10px;line-height:1.5}.option.correct{color:#1b5e20;font-weight:600}
    .opt-label{font-weight:700;margin-right:4px}.tick{color:#2e7d32;font-weight:700}
    .q-answer{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:4px;padding:7px 11px;margin-top:6px;line-height:1.6;font-size:10.5pt}
    .correct-ans{color:#1b5e20;font-weight:600}.blank-row,.match-row,.order-row{padding:2px 0}
    .sub-questions{padding-left:14px;border-left:3px solid #90caf9;margin-top:8px}
    .sub-questions .question{border-bottom:1px dashed #ddd;margin-bottom:12px;padding-bottom:10px}
    @media print{body{padding:12mm 15mm}.question{page-break-inside:avoid}}
    </style></head><body>
    <h1>${title}</h1><div class="subtitle">${subtitle}</div><hr/>${bodyHtml}
    </body></html>`;

    const blob = new Blob([pageHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) { URL.revokeObjectURL(url); toast.error("Popup blocked — allow popups and retry"); return; }
    win.addEventListener("load", () => { setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 300); });
  };

  // ── download PDF ──
  const handleDownloadPdf = (onlyApproved = false) => {
    const targetQuestions = onlyApproved
      ? questions.filter((_, i) => approvedSet.has(i))
      : questions;

    if (targetQuestions.length === 0) {
      toast.warning(onlyApproved ? "No approved questions to export" : "No questions to export");
      return;
    }

    const optionLabel = (idx) => String.fromCharCode(65 + idx);

    const renderAnswerHtml = (q) => {
      const ad = q.answer_data || {};
      const opts = q.options || [];

      if (["multiple-choice", "multiple-select", "true-false"].includes(q.type) && opts.length > 0) {
        const rows = opts
          .map((opt, i) => {
            const text = opt.text || opt.option_text || "";
            const correct = opt.is_correct;
            return `<div class="option${correct ? " correct" : ""}">
              <span class="opt-label">${optionLabel(i)}.</span> ${text}${correct ? " <span class='tick'>✓</span>" : ""}
            </div>`;
          })
          .join("");
        return `<div class="q-options">${rows}</div>`;
      }

      if (["fill-in-the-blank", "short-answer"].includes(q.type) && ad.correct_answer_text) {
        return `<div class="q-answer"><strong>Answer:</strong> ${ad.correct_answer_text}</div>`;
      }

      if (q.type === "essay" && ad.model_answer) {
        return `<div class="q-answer"><strong>Model Answer:</strong> ${ad.model_answer}</div>`;
      }

      if (q.type === "multi-fill" && Array.isArray(ad.correct_blanks)) {
        const labels = ad.blank_labels || [];
        const rows = ad.correct_blanks
          .map((blank, i) => {
            const ans = Array.isArray(blank) ? blank[0] : blank;
            const alts = Array.isArray(blank) && blank.length > 1 ? ` (also: ${blank.slice(1).join(", ")})` : "";
            const label = labels[i] ? `<strong>${labels[i]}</strong>` : `Blank ${i + 1}`;
            return `<div class="blank-row">${label}: <span class="correct-ans">${ans}</span>${alts}</div>`;
          })
          .join("");
        return `<div class="q-answer">${rows}</div>`;
      }

      if (q.type === "matching" && opts.length > 0) {
        const rows = opts
          .map((opt) => `<div class="match-row">${opt.text || opt.option_text || ""} → <span class="correct-ans">${opt.match_text || ""}</span></div>`)
          .join("");
        return `<div class="q-answer"><strong>Matching Pairs:</strong>${rows}</div>`;
      }

      if (q.type === "ordering" && opts.length > 0) {
        const rows = opts
          .map((opt, i) => `<div class="order-row"><strong>${i + 1}.</strong> ${opt.text || opt.option_text || ""}</div>`)
          .join("");
        return `<div class="q-answer"><strong>Correct Order:</strong>${rows}</div>`;
      }

      return "";
    };

    const renderQuestionBlock = (q, num) => {
      const typeLabel = q.type || "";
      const diffLabel = q.difficulty || "";
      const marks = q.default_marks ? `${q.default_marks}m` : "";
      const subtopic = q.topic ? `<span class="badge badge-sub">${q.topic}</span>` : "";
      const images = (() => {
        const urls = q.image_urls?.length ? q.image_urls : q.image_url ? [q.image_url] : [];
        if (!urls.length) return "";
        return `<div class="q-images">${urls.map((u) => `<img src="${u}" alt="question image" />`).join("")}</div>`;
      })();

      return `<div class="question">
        <div class="q-header">
          <span class="q-num">Q${num}</span>
          ${typeLabel ? `<span class="badge badge-type">${typeLabel}</span>` : ""}
          ${diffLabel ? `<span class="badge badge-diff">${diffLabel}</span>` : ""}
          ${marks ? `<span class="badge badge-marks">${marks}</span>` : ""}
          ${subtopic}
        </div>
        <div class="q-text">${q.question_text || "<em>(no text)</em>"}</div>
        ${images}
        ${renderAnswerHtml(q)}
      </div>`;
    };

    let bodyHtml = "";
    targetQuestions.forEach((q, localIdx) => {
      const origIdx = onlyApproved
        ? questions.indexOf(q)
        : localIdx;
      const num = origIdx + 1;

      if (q.type === "grouped") {
        const subImages = (() => {
          const urls = q.image_urls?.length ? q.image_urls : q.image_url ? [q.image_url] : [];
          return urls.length
            ? `<div class="q-images">${urls.map((u) => `<img src="${u}" alt="stem image" />`).join("")}</div>`
            : "";
        })();
        bodyHtml += `<div class="question grouped">
          <div class="q-header">
            <span class="q-num">Q${num}</span>
            <span class="badge badge-type">grouped</span>
          </div>
          <div class="q-text q-stem"><strong>Shared Stem:</strong><br>${q.question_text || ""}</div>
          ${subImages}
          <div class="sub-questions">`;
        (q.sub_questions || []).forEach((sq, si) => {
          bodyHtml += renderQuestionBlock(sq, `${num}.${si + 1}`);
        });
        bodyHtml += `</div></div>`;
      } else {
        bodyHtml += renderQuestionBlock(q, num);
      }
    });

    const title = jobData?.exam_title || jobData?.source_filename || `Similar Paper Job #${jobData?.id}`;
    const subtitle = [
      jobData?.exam_subject,
      jobData?.school_name,
      `Difficulty: ${jobData?.difficulty || "Mixed"}`,
      `Source: ${jobData?.source || "AI"}`,
      `${targetQuestions.length} question${targetQuestions.length !== 1 ? "s" : ""}`,
    ]
      .filter(Boolean)
      .join(" · ");

    const pageHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #111; padding: 18mm 20mm; }
    h1 { font-size: 15pt; margin-bottom: 4px; }
    .subtitle { font-size: 9pt; color: #555; margin-bottom: 12px; }
    hr { border: none; border-top: 1px solid #bbb; margin-bottom: 16px; }
    .question { margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #ddd; page-break-inside: avoid; }
    .question.grouped { border-bottom: 2px solid #999; }
    .q-header { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin-bottom: 6px; }
    .q-num { font-weight: 700; font-size: 12pt; }
    .badge { display: inline-block; padding: 1px 7px; border-radius: 3px; font-size: 8.5pt; }
    .badge-type { background: #e3f2fd; color: #1565c0; }
    .badge-diff { background: #fff3e0; color: #e65100; }
    .badge-marks { background: #f3e5f5; color: #6a1b9a; }
    .badge-sub { background: #e8f5e9; color: #1b5e20; }
    .q-text { margin-bottom: 8px; line-height: 1.6; }
    .q-stem { background: #f5f5f5; padding: 8px 10px; border-radius: 4px; margin-bottom: 8px; }
    .q-images { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
    .q-images img { max-width: 240px; max-height: 180px; object-fit: contain; border: 1px solid #ddd; border-radius: 3px; }
    .q-options { margin-bottom: 8px; }
    .option { padding: 2px 0 2px 10px; line-height: 1.5; }
    .option.correct { color: #1b5e20; font-weight: 600; }
    .opt-label { font-weight: 700; margin-right: 4px; }
    .tick { color: #2e7d32; font-weight: 700; }
    .q-answer { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 4px; padding: 7px 11px; margin-top: 6px; line-height: 1.6; font-size: 10.5pt; }
    .correct-ans { color: #1b5e20; font-weight: 600; }
    .blank-row, .match-row, .order-row { padding: 2px 0; }
    .sub-questions { padding-left: 14px; border-left: 3px solid #90caf9; margin-top: 8px; }
    .sub-questions .question { border-bottom: 1px dashed #ddd; margin-bottom: 12px; padding-bottom: 10px; }
    @media print {
      body { padding: 12mm 15mm; }
      .question { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="subtitle">${subtitle}</div>
  <hr />
  ${bodyHtml}
</body>
</html>`;

    const blob = new Blob([pageHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Popup blocked — please allow popups and try again");
      return;
    }
    win.addEventListener("load", () => {
      setTimeout(() => {
        win.print();
        URL.revokeObjectURL(url);
      }, 300);
    });
  };

  // ── derived ──
  const currentQ = questions[currentIndex] || null;
  const approvedCount = approvedSet.size;
  const isGrouped = currentQ?.type === "grouped";
  const subQuestions = isGrouped ? currentQ?.sub_questions || [] : [];
  // Comprehension passage: grouped question with passage_type="comprehension" or long question_text (>400 chars)
  const isComprehensionPassage =
    isGrouped &&
    (currentQ?.passage_type === "comprehension" ||
      (currentQ?.question_text?.length ?? 0) > 400);

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — LIST MODE
  // ════════════════════════════════════════════════════════════════════════

  if (mode === "list") {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Similar Papers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and import AI-generated similar paper questions into the Question Bank.
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="Search"
              placeholder="Paper name, subject, school..."
              value={jobsSearchInput}
              onChange={(e) => setJobsSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateListParams({ search: jobsSearchInput || null, page: 1 });
                }
              }}
              sx={{ minWidth: 260 }}
            />
            <Button variant="contained" onClick={() => updateListParams({ search: jobsSearchInput || null, page: 1 })}>
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={jobsStatus}
                label="Status"
                onChange={(e) => updateListParams({ status: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={jobsSource}
                label="Source"
                onChange={(e) => updateListParams({ source: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All sources</MenuItem>
                <MenuItem value="ai">AI</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={jobsDifficulty}
                label="Difficulty"
                onChange={(e) => updateListParams({ difficulty: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All difficulties</MenuItem>
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
                <MenuItem value="Mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Verification</InputLabel>
              <Select
                value={jobsVerificationStatus}
                label="Verification"
                onChange={(e) => updateListParams({ verification_status: e.target.value || null, page: 1 })}
              >
                <MenuItem value="">All verification</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="issues_found">Issues found</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        <Paper sx={{ width: "100%" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Original Paper</strong></TableCell>
                  <TableCell><strong>Subject</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Questions</strong></TableCell>
                  <TableCell><strong>Difficulty</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Verification</strong></TableCell>
                  <TableCell><strong>Generation Info</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobsLoading
                  ? [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(11)].map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : jobs.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No similar papers generated yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )
                  : jobs.map((job) => (
                      <TableRow key={job.id} hover>
                        <TableCell>{job.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {job.exam_title || job.source_filename || `Import #${job.import_id}`}
                          </Typography>
                          {job.school_name && (
                            <Typography variant="caption" color="text.secondary">
                              {job.school_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {job.exam_subject && (
                            <Chip label={job.exam_subject} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.source || "ai"}
                            size="small"
                            color={job.source === "bank" ? "info" : job.source === "mixed" ? "warning" : "default"}
                          />
                        </TableCell>
                        <TableCell>{job.questions_count}</TableCell>
                        <TableCell>
                          {job.difficulty && <DifficultyChip difficulty={job.difficulty} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            size="small"
                            color={
                              job.status === "completed" ? "success"
                              : job.status === "failed" ? "error"
                              : "warning"
                            }
                          />
                          {job.imported_question_ids?.length > 0 && (
                            <Chip label="Imported" size="small" color="info" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {job.verification_status ? (
                            <Tooltip
                              title={
                                job.verification_status === "issues_found" && job.verification_report
                                  ? buildVerificationSummary(job.verification_report)
                                  : job.verification_status
                              }
                              arrow
                            >
                              <Chip
                                label={job.verification_status === "issues_found" ? "Issues found" : "Passed"}
                                size="small"
                                color={job.verification_status === "passed" ? "success" : "warning"}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {job.generation_metadata?.style?.source || "legacy"}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {job.generation_metadata?.images?.provider_order?.join(" -> ") || "default"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={new Date(job.created_at).toLocaleString()}>
                            <Typography variant="caption">
                              {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Review & Import">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openReview(job.id)}
                                disabled={job.status !== "completed"}
                              >
                                <Visibility />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {job.import_id && (
                            <Tooltip title={job.status === "failed" ? "Retry: open original import" : "View original import"}>
                              <IconButton
                                size="small"
                                color={job.status === "failed" ? "warning" : "default"}
                                onClick={() => navigate(`/admin/question-bank/import/${job.import_id}/review`)}
                              >
                                {job.status === "failed"
                                  ? <AutoAwesome fontSize="small" />
                                  : <OpenInNew fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!jobsLoading && (
            <TablePagination
              component="div"
              count={jobsTotal}
              page={jobsPage}
              onPageChange={(_, nextPage) => updateListParams({ page: nextPage + 1 })}
              rowsPerPage={jobsPerPage}
              onRowsPerPageChange={(e) => updateListParams({ per_page: e.target.value, page: 1 })}
              rowsPerPageOptions={[15, 25, 50]}
            />
          )}
        </Paper>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — REVIEW MODE
  // ════════════════════════════════════════════════════════════════════════

  if (reviewLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!jobData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Job not found</Alert>
        <Button sx={{ mt: 2 }} onClick={backToList}>Back to list</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBack />} onClick={backToList} size="small">
          Back
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Similar Paper Review — Job #{jobData.id}
        </Typography>
        {jobData.import_id && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={() => navigate(`/admin/question-bank/import/${jobData.import_id}/review`)}
          >
            Original Paper
          </Button>
        )}
        {jobData.import_id && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AutoAwesome />}
            onClick={() => setGenOpen(true)}
          >
            Generate Similar Paper
          </Button>
        )}
      </Box>

      {/* Meta */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Chip label={`Source: ${jobData.source || "ai"}`} size="small" />
          <Chip label={`Difficulty: ${jobData.difficulty || "Mixed"}`} size="small" />
          <Chip label={`${questions.length} questions`} size="small" color="primary" />
          <Chip
            label={`${approvedCount} approved`}
            size="small"
            color={approvedCount > 0 ? "success" : "default"}
          />
          {jobData.imported_question_ids?.length > 0 && (
            <Alert severity="info" sx={{ py: 0, flexGrow: 1 }}>
              Already imported {jobData.imported_question_ids.length} questions.
            </Alert>
          )}
        </Box>
      </Paper>

      {questions.length === 0 ? (
        <Alert severity="warning">No questions in this job.</Alert>
      ) : (
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* ── Sidebar: question list ── */}
          <Paper sx={{ width: 260, minWidth: 260, maxHeight: "75vh", overflow: "auto", p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, px: 1 }}>
              <Typography variant="subtitle2">Questions</Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button size="small" onClick={approveAll}>Approve All</Button>
                <Button size="small" color="inherit" onClick={rejectAll}>Reject All</Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {questions.map((q, idx) => (
              <Box
                key={idx}
                onClick={() => goTo(idx)}
                sx={{
                  p: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  borderRadius: 1,
                  border: idx === currentIndex ? "2px solid #1976d2" : "1px solid #e0e0e0",
                  bgcolor: idx === currentIndex ? "#e3f2fd" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24 }}>
                  {idx + 1}
                </Typography>
                <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                  <Typography variant="caption" noWrap>
                    {stripHtml(q.question_text).slice(0, 60) || "(no text)"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                    {q.type && (
                      <Chip
                        label={q.type}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: typeColors[q.type] || "#999", color: "#fff" }}
                      />
                    )}
                    {modifiedSet.has(idx) && (
                      <Chip label="Edited" size="small" sx={{ height: 18, fontSize: 10 }} color="warning" />
                    )}
                    {q.topic && (
                      <Chip label={q.topic} size="small" variant="outlined" color="secondary" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Box>
                </Box>
                {approvedSet.has(idx) ? (
                  <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                ) : (
                  <Cancel sx={{ color: "#ccc", fontSize: 20 }} />
                )}
              </Box>
            ))}
          </Paper>

          {/* ── Main content ── */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Nav bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton size="small" disabled={currentIndex === 0} onClick={() => goTo(currentIndex - 1)}>
                <ArrowBack />
              </IconButton>
              <Typography variant="body2">
                Question {currentIndex + 1} of {questions.length}
              </Typography>
              <IconButton size="small" disabled={currentIndex === questions.length - 1} onClick={() => goTo(currentIndex + 1)}>
                <ArrowForward />
              </IconButton>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                variant={approvedSet.has(currentIndex) ? "contained" : "outlined"}
                color={approvedSet.has(currentIndex) ? "success" : "inherit"}
                onClick={() => toggleApproval(currentIndex)}
              >
                {approvedSet.has(currentIndex) ? "Approved" : "Approve"}
              </Button>
              {!editMode && (
                <Tooltip title="Re-roll just this question with the AI (keeps the rest of the paper)">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={regeneratingIdx === currentIndex ? <CircularProgress size={14} /> : <Autorenew />}
                      onClick={() => handleRegenerate(currentIndex)}
                      disabled={regeneratingIdx !== null}
                    >
                      {regeneratingIdx === currentIndex ? "Regenerating…" : "Regenerate"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!editMode && (
                <Button size="small" variant="outlined" startIcon={<Edit />} onClick={startEdit}>
                  Edit
                </Button>
              )}
            </Box>

            {/* Question content */}
            <Paper sx={{ p: 2, border: "1px dashed #ccc", minHeight: 300 }}>
              {editMode && editDraft ? (
                /* ── Edit panel ── */
                <Box>
                  <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editDraft.type || ""}
                        label="Type"
                        onChange={(e) => updateDraftField("type", e.target.value)}
                      >
                        {QUESTION_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={editDraft.difficulty || ""}
                        label="Difficulty"
                        onChange={(e) => updateDraftField("difficulty", e.target.value)}
                      >
                        <MenuItem value="Easy">Easy</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Hard">Hard</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Marks"
                      type="number"
                      value={editDraft.default_marks ?? ""}
                      onChange={(e) => updateDraftField("default_marks", parseInt(e.target.value) || 1)}
                      sx={{ width: 80 }}
                    />
                    <TextField
                      size="small"
                      label="Topic"
                      value={editDraft.topic ?? ""}
                      onChange={(e) => updateDraftField("topic", e.target.value)}
                      sx={{ width: 180 }}
                    />
                  </Box>
                  <TextField
                    multiline
                    fullWidth
                    minRows={4}
                    label="Question Text (HTML)"
                    value={editDraft.question_text || ""}
                    onChange={(e) => updateDraftField("question_text", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {/* Options for choice types */}
                  {["multiple-choice", "multiple-select", "true-false"].includes(editDraft.type) &&
                    Array.isArray(editDraft.options) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                        {editDraft.options.map((opt, oi) => (
                          <Box key={oi} sx={{ display: "flex", gap: 1, mb: 0.5, alignItems: "center" }}>
                            <Chip
                              label={String.fromCharCode(65 + oi)}
                              size="small"
                              color={opt.is_correct ? "success" : "default"}
                              onClick={() => {
                                const updated = { ...editDraft };
                                const opts = [...updated.options];
                                if (editDraft.type === "multiple-choice" || editDraft.type === "true-false") {
                                  opts.forEach((o, i) => (o.is_correct = i === oi));
                                } else {
                                  opts[oi] = { ...opts[oi], is_correct: !opts[oi].is_correct };
                                }
                                updated.options = opts;
                                setEditDraft(updated);
                              }}
                              sx={{ cursor: "pointer" }}
                            />
                            <TextField
                              size="small"
                              fullWidth
                              value={opt.text || opt.option_text || ""}
                              onChange={(e) => {
                                const updated = { ...editDraft };
                                const opts = [...updated.options];
                                opts[oi] = { ...opts[oi], text: e.target.value, option_text: e.target.value };
                                updated.options = opts;
                                setEditDraft(updated);
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  {/* Answer text for fill/short-answer */}
                  {["fill-in-the-blank", "short-answer"].includes(editDraft.type) && (
                    <TextField
                      size="small"
                      fullWidth
                      label="Correct Answer"
                      value={editDraft.answer_data?.correct_answer_text || ""}
                      onChange={(e) =>
                        updateDraftField("answer_data", {
                          ...editDraft.answer_data,
                          correct_answer_text: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  )}
                  {editDraft.type === "essay" && (
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      label="Model Answer"
                      value={editDraft.answer_data?.model_answer || ""}
                      onChange={(e) =>
                        updateDraftField("answer_data", {
                          ...editDraft.answer_data,
                          model_answer: e.target.value,
                        })
                      }
                      sx={{ mb: 2 }}
                    />
                  )}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="contained" startIcon={<Save />} onClick={saveEdit}>
                      Save
                    </Button>
                    <Button variant="outlined" startIcon={<Close />} onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : currentQ ? (
                /* ── Display panel ── */
                <Box>
                  {/* Meta chips */}
                  <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={`Q${currentIndex + 1}`}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    {currentQ.type && (
                      <Chip
                        label={QUESTION_TYPES.find((t) => t.value === currentQ.type)?.label || currentQ.type}
                        size="small"
                        sx={{ bgcolor: typeColors[currentQ.type] || "#999", color: "#fff" }}
                      />
                    )}
                    {currentQ.difficulty && <DifficultyChip difficulty={currentQ.difficulty} />}
                    {currentQ.default_marks && (
                      <Chip label={`${currentQ.default_marks} mark${currentQ.default_marks > 1 ? "s" : ""}`} size="small" />
                    )}
                    {currentQ.topic && (
                      <Chip label={currentQ.topic} size="small" variant="outlined" color="secondary" />
                    )}
                    {!isGrouped && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                        disabled={generatingDiagram}
                        onClick={() => handleAiGenerateDiagram({ type: "standalone" })}
                      >
                        AI diagram
                      </Button>
                    )}
                  </Box>

                  {/* Grouped question */}
                  {isGrouped ? (
                    <Box>
                      {/* Stem */}
                      <Paper sx={{ p: 2, mb: 2, bgcolor: "#f5f5f5" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1, flexWrap: "wrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {isComprehensionPassage ? "Comprehension Passage" : "Shared Stem"}
                            </Typography>
                            {isComprehensionPassage && (
                              <Chip label="Comprehension" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                            )}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                            disabled={generatingDiagram}
                            onClick={() => handleAiGenerateDiagram({ type: "stem" })}
                          >
                            AI diagram
                          </Button>
                        </Box>
                        {isComprehensionPassage ? (
                          <PassageWithLineNumbers text={currentQ.question_text || ""} />
                        ) : (
                          <HtmlWithLatex html={currentQ.question_text || ""} />
                        )}
                        {collectImageUrls(currentQ).map((url) => (
                          <Box key={url} sx={{ mt: 1 }}>
                            <img
                              src={url}
                              alt="Question"
                              style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 4 }}
                            />
                          </Box>
                        ))}
                      </Paper>
                      {/* Sub-questions */}
                      {subQuestions.map((sq, si) => (
                        <Paper key={si} sx={{ p: 2, mb: 1.5, border: "1px solid #e0e0e0" }}>
                          <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip label={`${currentIndex + 1}.${si + 1}`} size="small" />
                            {sq.type && (
                              <Chip
                                label={sq.type}
                                size="small"
                                sx={{ bgcolor: typeColors[sq.type] || "#999", color: "#fff", fontSize: 11 }}
                              />
                            )}
                            {sq.default_marks && (
                              <Chip label={`${sq.default_marks}m`} size="small" />
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                              disabled={generatingDiagram}
                              onClick={() => handleAiGenerateDiagram({ type: "sub", subIndex: si })}
                            >
                              AI diagram
                            </Button>
                          </Box>
                          <QuestionPaperLayout
                            question={sq}
                            questionNumber={`${currentIndex + 1}.${si + 1}`}
                            showAnswers={false}
                          />
                          {questionHasAnswer(sq) && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                              <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                                Answer
                              </Typography>
                              <QuestionAnswerDisplay question={sq} />
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    /* Standalone question */
                    <Box>
                      <QuestionPaperLayout
                        question={currentQ}
                        questionNumber={currentIndex + 1}
                        showAnswers={false}
                      />
                      {questionHasAnswer(currentQ) && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                          <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                            Answer
                          </Typography>
                          <QuestionAnswerDisplay question={currentQ} />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No question selected</Typography>
              )}
            </Paper>

            {/* Footer actions */}
            <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {jobData?.download_filename && jobData?.import_id && (
                <Tooltip title="Download the generated paper PDF (formatted like the original)">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <Download />}
                    onClick={handleDownloadBackendPdf}
                    disabled={downloadingPdf}
                  >
                    {downloadingPdf ? "Downloading…" : "Download PDF"}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Download all questions with answers as printable PDF">
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleDownloadPdf(false)}
                  disabled={questions.length === 0}
                >
                  Download Q&amp;A PDF (All)
                </Button>
              </Tooltip>
              {approvedCount > 0 && (
                <Tooltip title="Download only approved questions with answers as printable PDF">
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Download />}
                    onClick={() => handleDownloadPdf(true)}
                  >
                    Download Q&amp;A PDF (Approved {approvedCount})
                  </Button>
                </Tooltip>
              )}
              <Button variant="outlined" onClick={saveDraft} disabled={submitting}>
                Save Draft
              </Button>
              <Tooltip title="Save questions to the bank, then create a Mock Exam, Past Paper, or Worksheet from them">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PostAdd />}
                  endIcon={<ArrowDropDown />}
                  onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                  disabled={questions.length === 0}
                >
                  Create As
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color="success"
                disabled={submitting || approvedCount === 0}
                onClick={() => setSaveDialogOpen(true)}
              >
                {submitting ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : null}
                Import {approvedCount} Question{approvedCount !== 1 ? "s" : ""} to Bank
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* AI Generate Diagram — preview & upload (same as Import Review) */}
      <Dialog open={diagramDialogOpen} onClose={() => !imageUploadingDiagram && setDiagramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>AI Generated Diagram</DialogTitle>
        <DialogContent dividers>
          {generatedDiagram?.svg ? (
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  width: "100%",
                  minHeight: 280,
                  overflow: "auto",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{ width: "100%", maxWidth: 520 }}
                  dangerouslySetInnerHTML={{
                    __html: generatedDiagram.svg.replace(
                      /<svg /,
                      '<svg style="width:100%;height:auto;display:block;" '
                    ),
                  }}
                />
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                Diagram shows blank spaces for students to fill in. Answers are shown separately below the question.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Generating diagram…
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagramDialogOpen(false)} disabled={imageUploadingDiagram}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={applyGeneratedDiagram}
            disabled={!generatedDiagram?.svg || imageUploadingDiagram}
            startIcon={imageUploadingDiagram ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          >
            {imageUploadingDiagram ? "Uploading…" : "Add Diagram to Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Generate Similar Paper — Settings Dialog ── */}
      <Dialog open={genOpen} onClose={() => !genning && setGenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Generate Similar Paper</DialogTitle>
        <DialogContent dividers>
          {/* ── Question Source ── */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              Question Source
            </FormLabel>
            <RadioGroup
              row
              value={genSource}
              onChange={(e) => {
                setGenSource(e.target.value);
                if (e.target.value !== "ai" && bankSearchResults.length === 0) {
                  searchBankQuestions("");
                }
              }}
            >
              <FormControlLabel value="ai" control={<Radio size="small" />} label="AI Generated" />
              <FormControlLabel value="bank" control={<Radio size="small" />} label="From Question Bank" />
              <FormControlLabel value="mixed" control={<Radio size="small" />} label="Mixed" />
              <FormControlLabel value="variation" control={<Radio size="small" />} label="Variation" />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {genSource === "ai"
                ? "AI creates similar questions matching the original paper format."
                : genSource === "bank"
                ? "Select existing questions from the question bank."
                : genSource === "variation"
                ? "Each selected question is rewritten with different names, numbers and wording — same skill, same difficulty, answer recomputed."
                : "Pick bank questions first, then AI fills the remainder."}
            </Typography>
          </FormControl>

          {/* ── Bank Question Picker (bank / mixed only) ── */}
          {genSource !== "ai" && (
            <Box sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search questions…"
                  value={bankSearchQuery}
                  onChange={(e) => {
                    setBankSearchQuery(e.target.value);
                    searchBankQuestions(e.target.value);
                  }}
                  fullWidth
                  InputProps={{
                    endAdornment: bankSearchLoading ? <CircularProgress size={14} /> : null,
                  }}
                />
                <Chip
                  label={`${selectedBankIds.length} selected`}
                  size="small"
                  color={selectedBankIds.length > 0 ? "primary" : "default"}
                  sx={{ alignSelf: "center", flexShrink: 0 }}
                />
              </Box>
              <List dense disablePadding sx={{ maxHeight: 200, overflowY: "auto" }}>
                {bankSearchResults.length === 0 && !bankSearchLoading && (
                  <ListItem>
                    <ListItemText secondary="No questions found. Try a different search." />
                  </ListItem>
                )}
                {bankSearchResults.map((q) => (
                  <ListItem key={q.id} disablePadding>
                    <ListItemButton dense onClick={() => toggleBankId(q.id)} selected={selectedBankIds.includes(q.id)}>
                      <Checkbox
                        edge="start"
                        checked={selectedBankIds.includes(q.id)}
                        tabIndex={-1}
                        size="small"
                        disableRipple
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ maxWidth: 350, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: "0.875rem", lineHeight: 1.35, wordBreak: "break-word" }}>
                            {q.question_text ? (
                              <HtmlWithLatex html={(q.question_text || "").replace(/<[^>]*>/g, "")} />
                            ) : (
                              <Typography component="span" variant="body2" color="text.secondary">(no text)</Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: "flex", gap: 0.5, mt: 0.3 }}>
                            <Chip label={q.type} size="small" sx={{ height: 16, fontSize: 10 }} />
                            <Chip label={q.difficulty || "Medium"} size="small" sx={{ height: 16, fontSize: 10 }} />
                            <Chip label={`${q.default_marks || 1}m`} size="small" sx={{ height: 16, fontSize: 10 }} />
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* ── Shuffle (bank / mixed only) ── */}
          {genSource !== "ai" && (
            <Box sx={{ mb: 2 }}>
              <FormLabel sx={{ fontSize: 13, fontWeight: 600 }}>Shuffle</FormLabel>
              <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={shuffleQuestions} onChange={(e) => setShuffleQuestions(e.target.checked)} />}
                  label={<Typography variant="body2">Shuffle question order</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={shuffleOptions} onChange={(e) => setShuffleOptions(e.target.checked)} />}
                  label={<Typography variant="body2">Shuffle MCQ option order</Typography>}
                />
              </Box>
            </Box>
          )}

          {/* ── Paper Settings ── */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
            <TextField
              label="Question count"
              type="number"
              size="small"
              value={genCount}
              onChange={(e) => {
                const v = Math.max(1, Math.min(100, +e.target.value));
                setGenCount(v);
                if (v > 15) setGenAsync(true); // large papers → recommend background
              }}
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 140 }}
              disabled={["bank", "variation"].includes(genSource)}
              helperText={["bank", "variation"].includes(genSource) ? "Set by selection" : "1–100"}
            />
            {genSource !== "bank" && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select value={genDifficulty} label="Difficulty" onChange={(e) => setGenDifficulty(e.target.value)}>
                  {["Mixed", "Easy", "Medium", "Hard"].map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={<Switch size="small" checked={genAsync} onChange={(e) => setGenAsync(e.target.checked)} />}
              label={<Typography variant="caption">Run in background</Typography>}
              sx={{ ml: 1 }}
            />
          </Box>

          {/* ── Topic focus hint ── */}
          {genSource !== "bank" && (
            <TextField
              label="Topic focus (optional)"
              size="small"
              fullWidth
              value={genTopicHint}
              onChange={(e) => setGenTopicHint(e.target.value)}
              placeholder="e.g. Fractions, Algebra, Comprehension"
              helperText="Bias the generated questions toward these topics where the original structure allows."
              sx={{ mb: 2 }}
            />
          )}

          <Box sx={{ mb: 2 }}>
            <CategorySaveFields
              categoryId={saveCategoryId}
              onCategoryChange={setSaveCategoryId}
              sourceType={saveSourceType}
              onSourceTypeChange={setSaveSourceType}
              disabled={genning}
              helperText="You can change these again before importing to the question bank."
            />
          </Box>

          {/* Async info */}
          {genAsync && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1, py: 0.5 }}>
              <Typography variant="caption">
                {genCount > 15
                  ? "Background processing is recommended for large papers — auto-enabled. You can close this dialog and check back later."
                  : "Enable background processing for large papers (20+ questions). You can close this dialog and check back later."}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenOpen(false)} disabled={genning}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleGenerate}
            disabled={genning || (genSource !== "ai" && selectedBankIds.length === 0)}
            startIcon={genning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          >
            {genning ? "Starting…" : "Generate →"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Generate Similar Paper — Result Dialog ── */}
      <Dialog
        open={genResultOpen}
        onClose={() => genStatus !== "pending" && genStatus !== "processing" && setGenResultOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {genStatus === "pending" || genStatus === "processing" ? "Generating Similar Paper…" : "Similar Paper Ready"}
        </DialogTitle>
        <DialogContent>
          {(genStatus === "pending" || genStatus === "processing") && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                  {genStatus === "pending" ? "Queued — waiting to start…" : `Generating… ${genProgress}%`}
                </Typography>
              </Box>
              <LinearProgress
                variant={genProgress > 0 ? "determinate" : "indeterminate"}
                value={genProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                You can close this dialog; generation continues in the background.
              </Typography>
            </Box>
          )}
          {genError && <Alert severity="error" sx={{ mb: 2 }}>{genError}</Alert>}
          {genStatus === "completed" && !genError && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {genQuestions.length} question{genQuestions.length !== 1 ? "s" : ""} generated successfully!
              {genJobId && (
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => { setGenResultOpen(false); openReview(genJobId); }}
                >
                  Open in Review
                </Button>
              )}
            </Alert>
          )}
          {genStatus === "completed" &&
            !genError &&
            (genVerification?.report?.answer_gaps > 0 ||
              genVerification?.report?.checks?.answer_completeness?.threshold_exceeded) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Answer key completeness:{" "}
                {genVerification?.report?.answer_gaps ??
                  genVerification?.report?.checks?.answer_completeness?.missing_count ??
                  0}{" "}
                slot(s) may be missing answer text. Open the PDF and verify the answer key.
              </Alert>
            )}
          {similarPaperSaved && (
            <Alert severity="info" sx={{ mb: 2 }}>
              ✓ {similarPaperSavedIds.length} questions saved to Question Bank
              {similarPaperSavedIds.length > 0 &&
                ` (IDs: ${similarPaperSavedIds.slice(0, 5).join(", ")}${similarPaperSavedIds.length > 5 ? "…" : ""})`}
            </Alert>
          )}
          {genQuestions.length > 0 && (
            <Box sx={{ maxHeight: 300, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {genQuestions.map((q, i) => {
                const plain = (q.question_text || "").replace(/<[^>]*>/g, "").trim();
                const display = plain.length > 0
                  ? plain.substring(0, 90) + (plain.length > 90 ? "…" : "")
                  : `[${q.type || "question"} — ${q.default_marks || 1}m]`;
                return (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 28, color: "text.secondary" }}>{i + 1}.</Typography>
                    <Typography variant="body2" sx={{ flex: 1, fontStyle: plain ? "normal" : "italic", color: plain ? "text.primary" : "text.secondary" }}>{display}</Typography>
                    {q.type && <Chip label={q.type} size="small" sx={{ height: 18, fontSize: 10 }} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {genStatus !== "pending" && genStatus !== "processing" && (
            <>
              <Button
                onClick={() => { setGenResultOpen(false); setGenOpen(true); }}
                sx={{ textTransform: "none" }}
              >
                Generate Another
              </Button>
              <Button onClick={() => setGenResultOpen(false)}>Close</Button>
            </>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSaveSimilarPaperToBank}
            disabled={
              savingSimilarPaper ||
              similarPaperSaved ||
              genStatus !== "completed" ||
              genQuestions.length === 0
            }
            startIcon={savingSimilarPaper ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {savingSimilarPaper ? "Saving…" : similarPaperSaved ? "✓ Saved to Question Bank" : "Save to Question Bank"}
          </Button>
          <Tooltip title="Download the generated paper PDF (formatted like the original)">
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={handleDownloadBackendPdf}
                disabled={downloadingPdf || genStatus !== "completed"}
              >
                {downloadingPdf ? "Downloading…" : "Download PDF"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download all questions with answers as printable PDF">
            <span>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadGenPdf}
                disabled={genQuestions.length === 0 || genStatus !== "completed"}
              >
                Download Q&amp;A PDF
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* ── Create As menu ── */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
      >
        <MenuItem onClick={() => openCreateDialog("mock_exam")}>Mock Exam</MenuItem>
        <MenuItem onClick={() => openCreateDialog("paper")}>Past Paper</MenuItem>
        <MenuItem onClick={() => openCreateDialog("worksheet")}>Worksheet</MenuItem>
      </Menu>

      {/* ── Create Entity dialog ── */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Create {CREATE_TARGET_LABELS[createTarget] || "Entity"}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption">
              Questions are saved to the Question Bank first, then a draft{" "}
              {CREATE_TARGET_LABELS[createTarget]?.toLowerCase()} is created. You'll be redirected to finish the rest of its settings.
            </Typography>
          </Alert>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mb: 2 }}
          />

          {(createTarget === "mock_exam" || createTarget === "paper") && (
            <>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Format *</InputLabel>
                <Select
                  label="Format *"
                  value={createForm.format_id}
                  onChange={(e) => setCreateForm((f) => ({ ...f, format_id: e.target.value }))}
                >
                  {lookupFormats.map((fmt) => (
                    <MenuItem key={fmt.id} value={fmt.id}>{fmt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>School Year *</InputLabel>
                <Select
                  label="School Year *"
                  value={createForm.school_year_id}
                  onChange={(e) => setCreateForm((f) => ({ ...f, school_year_id: e.target.value }))}
                >
                  {lookupSchoolYears.map((y) => (
                    <MenuItem key={y.id} value={y.id}>{y.name || y.year || y.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Price"
                type="number"
                size="small"
                value={createForm.price}
                onChange={(e) => setCreateForm((f) => ({ ...f, price: Math.max(0, +e.target.value) }))}
                inputProps={{ min: 0, step: "0.01" }}
                helperText="Defaults to 0 (free). Adjust later on the edit page."
                sx={{ width: 180 }}
              />
            </>
          )}

          {createTarget === "worksheet" && (
            <FormControl size="small" fullWidth>
              <InputLabel>Topic *</InputLabel>
              <Select
                label="Topic *"
                value={createForm.topic_id}
                onChange={(e) => setCreateForm((f) => ({ ...f, topic_id: e.target.value }))}
              >
                {lookupTopics.length === 0 && (
                  <MenuItem value="" disabled>
                    {lookupsLoading ? "Loading…" : "No topics found for this subject"}
                  </MenuItem>
                )}
                {lookupTopics.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.label || t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {lookupsLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Loading options…</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEntity}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <PostAdd />}
          >
            {creating ? "Creating…" : `Create ${CREATE_TARGET_LABELS[createTarget] || ""}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={saveDialogOpen}
        onClose={() => !submitting && setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import to Question Bank</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            {approvedCount} question{approvedCount !== 1 ? "s" : ""} will be added as new question
            bank questions.
          </Alert>
          <CategorySaveFields
            categoryId={saveCategoryId}
            onCategoryChange={setSaveCategoryId}
            sourceType={saveSourceType}
            onSourceTypeChange={setSaveSourceType}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? "Importing…" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SimilarPaperReview;
